/**
 * The three calls, as a port and as HTTP.
 *
 * A port because the engine that drives synchronisation has enough to be wrong about without a
 * network in it: every case in this package's suite hands it a transport that answers instantly
 * and exactly, which is what makes "a lost acknowledgement" and "a server that moved on" things a
 * test can *cause* rather than wait for.
 *
 * The HTTP implementation is the only thing here that knows a URL. Two rules in it are worth
 * stating, because both are decisions rather than details:
 *
 * **Every response is parsed, never trusted.** The schemas come from `@orbit-mind/validators/sync`
 * — the same file the Worker parses its requests with — so a server and a client that disagree
 * disagree in one place instead of two. An answer that does not match is a refusal, not a
 * document: a proxy that returns an HTML error page with a 200 on it is a real thing, and a client
 * that took it for a map would write it over the user's work.
 *
 * **A network that is not there is not an error to report loudly.** It comes back as
 * `NETWORK_UNAVAILABLE`, which the engine treats as "try again later" rather than as a failure the
 * user has to see. Being offline is a normal state in this product, not an incident.
 */
import { parseLibrary } from '@orbit-mind/domain';
import { appError, err, ok, } from '@orbit-mind/shared-types';
import { parseAt, syncHistoryResponseSchema, syncListResponseSchema, syncPullResponseSchema, syncPushResponseSchema, syncStateSchema, } from '@orbit-mind/validators';
function unreachable(cause) {
    return appError('NETWORK_UNAVAILABLE', 'the server could not be reached', {
        retryable: true,
        cause,
    });
}
/** What the server said went wrong, or a shape-agnostic refusal when it said nothing legible. */
async function refusal(response) {
    const fallback = appError('INTERNAL', `the server refused this request (${String(response.status)})`, {
        // 5xx and 429 are worth trying again; a 4xx means this request will be refused for ever.
        retryable: response.status >= 500 || response.status === 429,
        details: { status: response.status },
    });
    try {
        const body = await response.json();
        if (typeof body === 'object' && body !== null && 'code' in body && 'message' in body) {
            const { code, message } = body;
            return { ...fallback, code: code, message };
        }
    }
    catch {
        // A body that is not JSON tells us nothing the status has not already said.
    }
    return fallback;
}
/**
 * One call to the sync Worker: the account header, the cookie, and a JSON answer or a refusal.
 *
 * Shared by the map transport and the library organization's, so both treat a missing network, a
 * refusal and an answer that is not JSON the same way.
 */
async function sendJson(options, target, init) {
    let response;
    try {
        const headers = new Headers(init.headers);
        if (options.accountId != null)
            headers.set('x-orbit-account', options.accountId);
        response = await options.fetch(target, {
            ...init,
            headers,
            // The session cookie is what authenticates every one of these. Explicit, though it is the
            // default, because a request that quietly dropped it would be answered 401 for a reason
            // nothing about the code would show.
            credentials: 'same-origin',
        });
    }
    catch (cause) {
        return err(unreachable(cause));
    }
    if (!response.ok)
        return err(await refusal(response));
    try {
        return ok({ response, body: await response.json() });
    }
    catch (cause) {
        return err(appError('INTERNAL', 'the server answered with something that is not JSON', {
            retryable: false,
            cause,
        }));
    }
}
export function createHttpTransport(options) {
    const base = options.origin ?? '';
    const url = (mapId, suffix = '') => `${base}/sync/maps/${encodeURIComponent(mapId)}${suffix}`;
    const send = (target, init) => sendJson(options, target, init);
    return {
        list: async (listing) => {
            const sent = await send(`${base}/sync/maps${listing?.includeTrashed === true ? '?includeTrashed=true' : ''}`, {
                method: 'GET',
                headers: { accept: 'application/json' },
            });
            if (!sent.ok)
                return err(sent.error);
            const listed = parseAt(syncListResponseSchema, sent.value.body, 'network', 'map listing');
            if (!listed.ok)
                return err(listed.error);
            return ok(listed.value.maps);
        },
        register: async (mapId, document, operationIds) => {
            const sent = await send(url(mapId), {
                method: 'POST',
                headers: { 'content-type': 'application/json', accept: 'application/json' },
                body: JSON.stringify({ document, operationIds }),
            });
            if (!sent.ok)
                return err(sent.error);
            const state = parseAt(syncStateSchema, sent.value.body, 'network', 'register answer');
            if (!state.ok)
                return err(state.error);
            // 201 is "this call created it"; 200 is "it was already there". The distinction decides
            // whether the outbox may be considered sent — see `RegisterAnswer`.
            return ok({ state: state.value, created: sent.value.response.status === 201 });
        },
        pull: async (mapId) => {
            const sent = await send(url(mapId), {
                method: 'GET',
                headers: { accept: 'application/json' },
            });
            if (!sent.ok)
                return err(sent.error);
            const pulled = parseAt(syncPullResponseSchema, sent.value.body, 'network', 'pull answer');
            if (!pulled.ok)
                return err(pulled.error);
            // The document has been through the same schema the editor's own storage uses; the cast
            // describes what was checked, and `validators`' drift test is what keeps the two in step.
            return ok(pulled.value);
        },
        push: async (mapId, baseRevision, commands) => {
            const sent = await send(url(mapId, '/push'), {
                method: 'POST',
                headers: { 'content-type': 'application/json', accept: 'application/json' },
                body: JSON.stringify({ baseRevision, commands }),
            });
            if (!sent.ok)
                return err(sent.error);
            const answer = parseAt(syncPushResponseSchema, sent.value.body, 'network', 'push answer');
            if (!answer.ok)
                return err(answer.error);
            const { status, mapId: id, revision, updatedAt } = answer.value;
            const state = { mapId: id, revision, updatedAt };
            return ok(status === 'applied'
                ? { status, state, acknowledged: answer.value.acknowledged }
                : { status, state });
        },
        history: async (mapId) => {
            const sent = await send(url(mapId, '/versions'), {
                method: 'GET',
                headers: { accept: 'application/json' },
            });
            if (!sent.ok)
                return err(sent.error);
            const listed = parseAt(syncHistoryResponseSchema, sent.value.body, 'network', 'history');
            if (!listed.ok)
                return err(listed.error);
            return ok(listed.value);
        },
        restore: async (mapId, revision) => {
            const sent = await send(url(mapId, '/restore'), {
                method: 'POST',
                headers: { 'content-type': 'application/json', accept: 'application/json' },
                body: JSON.stringify({ revision }),
            });
            if (!sent.ok)
                return err(sent.error);
            const state = parseAt(syncStateSchema, sent.value.body, 'network', 'restore answer');
            if (!state.ok)
                return err(state.error);
            return ok(state.value);
        },
        setTrashed: async (mapId, trashed) => {
            const sent = await send(url(mapId, trashed ? '/trash' : '/untrash'), {
                method: 'POST',
                headers: { accept: 'application/json' },
            });
            if (!sent.ok)
                return err(sent.error);
            const state = parseAt(syncStateSchema, sent.value.body, 'network', 'trash answer');
            if (!state.ok)
                return err(state.error);
            return ok(state.value);
        },
    };
}
function organizationAnswer(body) {
    if (typeof body !== 'object' || body === null)
        return null;
    const { revision, organization } = body;
    if (typeof revision !== 'number' || !Number.isInteger(revision) || revision < 0)
        return null;
    if (organization === null)
        return { revision, organization: null };
    if (typeof organization !== 'object')
        return null;
    return { revision, organization: parseLibrary(organization) };
}
function notAnOrganization() {
    return appError('INTERNAL', 'the server answered with something that is not a library', {
        retryable: false,
    });
}
/**
 * `/sync/library` over HTTP. The answer is parsed by the same `parseLibrary` every local copy goes
 * through, so a server and a device that disagree about the shape drop the same fields.
 */
export function createHttpOrganizationTransport(options) {
    const target = `${options.origin ?? ''}/sync/library`;
    return {
        read: async () => {
            const sent = await sendJson(options, target, {
                method: 'GET',
                headers: { accept: 'application/json' },
            });
            if (!sent.ok)
                return err(sent.error);
            const answer = organizationAnswer(sent.value.body);
            return answer === null ? err(notAnOrganization()) : ok(answer);
        },
        write: async (baseRevision, organization) => {
            const sent = await sendJson(options, target, {
                method: 'POST',
                headers: { 'content-type': 'application/json', accept: 'application/json' },
                body: JSON.stringify({ baseRevision, organization }),
            });
            if (!sent.ok)
                return err(sent.error);
            const body = sent.value.body;
            if (body.status === 'applied' && typeof body.revision === 'number')
                return ok({ status: 'applied', revision: body.revision });
            if (body.status === 'behind') {
                const answer = organizationAnswer(body);
                if (answer !== null)
                    return ok({ status: 'behind', ...answer });
            }
            return err(notAnOrganization());
        },
    };
}
//# sourceMappingURL=transport.js.map